import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import ConfirmDialog from "../../components/ConfirmDialog";
import { PUBLIC_PAGE_OPTIONS } from "../../components/PageAccessGuard";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { ADMIN_EMAILS } from "../../lib/platformData";
import { mergeSiteContent } from "../../utils/helpers";

const statusLabels = {
  active: "Actif",
  disabled: "Désactivé",
  deleted: "Supprimé",
};

function statusClass(status) {
  if (status === "active") return "border-emerald-500/35 bg-emerald-500/10 text-emerald-100";
  if (status === "deleted") return "border-red-500/35 bg-red-500/10 text-red-100";
  return "border-yellow-500/35 bg-yellow-500/10 text-yellow-100";
}

function PageEditor({ item, access, onSave, onRequestDelete }) {
  const [message, setMessage] = useState(access.message || "");

  useEffect(() => {
    setMessage(access.message || "");
  }, [access.message]);

  return (
    <article className="rounded-lg border border-white/10 bg-black p-4">
      <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-black uppercase">{item.label}</h2>
            <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase ${statusClass(access.status)}`}>
              {statusLabels[access.status] || statusLabels.active}
            </span>
          </div>
          <p className="mt-2 text-sm text-gray-500">{item.routes.join(", ")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onSave(item.key, { ...access, status: "active", message })}
            className="rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black transition hover:bg-emerald-500 hover:text-white"
          >
            Réactiver
          </button>
          <button
            onClick={() => onSave(item.key, { ...access, status: "disabled", message })}
            className="rounded-full border border-yellow-500/45 px-4 py-2 text-xs font-bold uppercase text-yellow-100 transition hover:bg-yellow-500 hover:text-black"
          >
            Désactiver
          </button>
          <button
            onClick={() => onRequestDelete({ item, access: { ...access, message } })}
            className="rounded-full bg-red-600 px-4 py-2 text-xs font-bold uppercase transition hover:bg-red-700"
          >
            Supprimer
          </button>
        </div>
      </div>
      <label className="mt-4 block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">
          Message visiteur
        </span>
        <div className="flex flex-col gap-2 md:flex-row">
          <input
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className="min-w-0 flex-1 rounded border border-white/10 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-red-500"
          />
          <button
            onClick={() => onSave(item.key, { ...access, message })}
            className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase transition hover:border-white"
          >
            Sauvegarder le message
          </button>
        </div>
      </label>
    </article>
  );
}

export default function AdminPages() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const toast = useToast();
  const [content, setContent] = useState(() => mergeSiteContent());
  const [pendingDelete, setPendingDelete] = useState(null);
  const isAdmin = Boolean(user && ADMIN_EMAILS.includes(user.email));

  useEffect(() => {
    if (!isAdmin) return undefined;
    return onSnapshot(doc(db, "admin", "content"), (snap) => {
      setContent(mergeSiteContent(snap.exists() ? snap.data() : {}));
    });
  }, [isAdmin]);

  const savePage = async (key, access) => {
    const next = {
      ...content,
      pageAccess: {
        ...content.pageAccess,
        [key]: {
          status: access.status || "active",
          message: access.message || "Cette page est momentanément indisponible.",
        },
      },
    };
    setContent(next);
    try {
      await setDoc(doc(db, "admin", "content"), sanitizeForFirestore(next), { merge: true });
      const status = access.status || "active";
      toast.success(
        status === "active"
          ? "Page réactivée"
          : status === "deleted"
            ? "Page marquée supprimée"
            : "Page désactivée"
      );
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    await savePage(pendingDelete.item.key, {
      ...pendingDelete.access,
      status: "deleted",
    });
    setPendingDelete(null);
  };

  if (loading) return <main className="min-h-screen bg-black p-8 text-white">Chargement...</main>;

  if (!user || !isAdmin) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Admin Pages</h1>
          <p className="mt-3 text-gray-400">Connexion admin requise.</p>
          {user && !isAdmin ? (
            <button onClick={logout} className="mt-6 rounded-full bg-red-600 px-5 py-3 text-sm font-bold uppercase">
              Changer de compte
            </button>
          ) : (
            <button onClick={loginWithGoogle} className="mt-6 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">
              Connexion Google
            </button>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black p-4 text-white md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">Admin paramètres</p>
            <h1 className="mt-2 text-4xl font-black uppercase">Pages du site</h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-400">
              Désactive, marque supprimée ou réactive chaque page publique. Les contenus restent conservés pour permettre une réactivation immédiate.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/" className="rounded-full bg-white px-4 py-2 text-sm font-bold uppercase text-black">Voir le site</Link>
            <Link to="/admin/mentions-legales" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">Mentions légales</Link>
            <Link to="/admin/typographie" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">Typographie</Link>
            <Link to="/admin" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">Dashboard</Link>
          </div>
        </header>

        <section className="grid grid-cols-1 gap-4">
          {PUBLIC_PAGE_OPTIONS.map((item) => (
            <PageEditor
              key={item.key}
              item={item}
              access={content.pageAccess[item.key]}
              onSave={savePage}
              onRequestDelete={setPendingDelete}
            />
          ))}
        </section>
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Supprimer cette page ?"
        message="La page ne sera plus accessible aux visiteurs. Son contenu restera conservé dans l'admin pour permettre une réactivation ultérieure."
        confirmLabel="Marquer supprimée"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </main>
  );
}
